import { AppShell } from '@/components/layout/AppShell';
import { useEffect, useState } from 'react';
import { api, parseErr } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { FolderTree, Folder, Users as UsersIcon, ShieldCheck, PlayCircle, CheckCircle2, AlertTriangle, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';

const ROLES = ['owner', 'dm_creative', 'dm_technical', 'dm_architecture', 'operations', 'accounts', 'commercials', 'hr', 'team'];
const LEVELS = ['edit', 'view', 'none'];

export default function DriveSetupWizardPage() {
  const [state, setState] = useState(null);
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [dryRun, setDryRun] = useState(null);
  const [mockInput, setMockInput] = useState({
    mock_project_code: 'P-DEMO-001',
    mock_client_name: 'Demo Client Pvt Ltd',
    mock_project_name: 'Demo Wayfinding Project',
  });
  const [teamGroupEdits, setTeamGroupEdits] = useState({});
  const [categoryJId, setCategoryJId] = useState(null);

  const load = async () => {
    try {
      const r = await api.get('/admin/drive_setup');
      setState(r.data); setStep(r.data.current_step || 1);
      const tg = {};
      (r.data.team_groups || []).forEach(g => { tg[g.role] = { google_group: g.google_group || '', member_emails: (g.member_emails || []).join(', ') }; });
      setTeamGroupEdits(tg);
    } catch (e) { toast.error(parseErr(e)); }
    // SOP Category J — Drive Access & Setup — is the canonical help content for this wizard.
    try {
      const t = await api.get('/knowledge/tree');
      const catJ = (t.data || []).find(p => p.kind === 'folder' && (p.metadata || {}).sop_category === 'J');
      if (catJ) setCategoryJId(catJ.id);
    } catch (e) { console.debug('sop-category-J lookup skipped:', e?.response?.status || e); }
  };
  useEffect(() => { load(); }, []);

  const saveStep = async (nextStep, extra = {}) => {
    setSaving(true);
    try {
      const body = { current_step: nextStep, ...extra };
      await api.put('/admin/drive_setup', body);
      if (nextStep === 5 && extra.completed) toast.success('Drive setup marked complete');
      setStep(nextStep); await load();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setSaving(false); }
  };

  const runDry = async () => {
    try {
      const r = await api.post('/admin/drive_setup/dry_run', mockInput);
      setDryRun(r.data);
    } catch (e) { toast.error(parseErr(e)); }
  };

  const patchGrid = async (folder, role, value) => {
    const newGrid = { ...state.access_template, [folder]: { ...state.access_template[folder], [role]: value } };
    try {
      await api.put('/admin/settings/access_template', { grid: newGrid });
      setState({ ...state, access_template: newGrid });
    } catch (e) { toast.error(parseErr(e)); }
  };

  const saveTeamGroup = async (role) => {
    const rec = teamGroupEdits[role] || {};
    try {
      await api.put(`/admin/settings/team_groups/${role}`, {
        role,
        google_group: rec.google_group || null,
        member_emails: (rec.member_emails || '').split(',').map(s => s.trim()).filter(Boolean),
      });
      toast.success(`Team group for ${role} saved`); await load();
    } catch (e) { toast.error(parseErr(e)); }
  };

  if (!state) return <AppShell><div className="p-8">Loading…</div></AppShell>;

  return (
    <AppShell breadcrumbs={[{ label: 'Admin', to: '/admin/users' }, { label: 'Drive Setup Wizard' }]}>
      <div className="p-6 max-w-[1100px] mx-auto space-y-6 fade-up" data-testid="drive-setup-wizard">
        <div>
          <h1 className="font-heading text-3xl font-semibold flex items-center gap-2"><FolderTree size={26} />Drive Setup Wizard</h1>
          <p className="text-[hsl(var(--foreground-muted))] text-[14px] mt-1">
            Configure how 32DECEMBER OS should provision Drive folders + access.{' '}
            <span className="chip chip-warning ml-1">Reference-only mode</span> — actual folder creation activates when Google Drive OAuth is turned on.
          </p>
          {state.completed && <div className="mt-2 inline-flex items-center gap-2 chip chip-success"><CheckCircle2 size={13} />Setup complete</div>}
          {categoryJId && (
            <Link to={`/knowledge/${categoryJId}`}
                  className="mt-2 ml-2 inline-flex items-center gap-1 chip hover:bg-[hsl(var(--surface-2))]"
                  data-testid="drive-wizard-sop-j-link">
              <BookOpen size={11} />SOP Category J — full procedures
            </Link>
          )}
        </div>

        {/* STEPPER */}
        <div className="flex items-center gap-1 flex-wrap" data-testid="drive-stepper">
          {[1, 2, 3, 4, 5].map(n => (
            <button key={n}
                    onClick={() => setStep(n)}
                    data-testid={`drive-step-btn-${n}`}
                    className={`px-3 py-1.5 rounded-md text-[12.5px] border ${step === n ? 'bg-[hsl(var(--primary))] text-white border-transparent' : 'bg-[hsl(var(--card))]'}`}
                    style={{ borderColor: 'hsl(var(--border))' }}>
              Step {n}: {['Root folder', 'Naming', 'Access template', 'Team groups', 'Test run'][n - 1]}
            </button>
          ))}
        </div>

        {/* STEP 1 */}
        {step === 1 && (
          <section className="card p-5" data-testid="drive-step-1">
            <div className="flex items-start gap-3">
              <Folder size={22} className="text-[hsl(var(--foreground-muted))] mt-1" />
              <div className="flex-1">
                <div className="font-heading font-semibold">Root parent folder</div>
                <div className="text-[13px] text-[hsl(var(--foreground-muted))] mt-0.5">The single Drive location under which all new project folders will be created.</div>
                <label className="block mt-3"><span className="text-[12px] text-[hsl(var(--foreground-muted))]">Drive folder URL</span>
                  <Input value={state.root_parent_url || ''} onChange={e => setState({ ...state, root_parent_url: e.target.value })} placeholder="https://drive.google.com/drive/folders/…" data-testid="drive-root-input" />
                </label>
                <div className="mt-3">
                  <Button size="sm" onClick={() => saveStep(2, { root_parent_url: state.root_parent_url })} disabled={!state.root_parent_url || saving} data-testid="drive-step-1-next">Save &amp; continue</Button>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <section className="card p-5" data-testid="drive-step-2">
            <div className="font-heading font-semibold">Project folder naming convention</div>
            <div className="text-[13px] text-[hsl(var(--foreground-muted))] mt-0.5">
              Tokens: <code className="text-[11.5px]">{'{PROJECT_CODE}'}</code>, <code className="text-[11.5px]">{'{CLIENT_NAME}'}</code>, <code className="text-[11.5px]">{'{PROJECT_NAME}'}</code>
            </div>
            <label className="block mt-3"><span className="text-[12px] text-[hsl(var(--foreground-muted))]">Naming template</span>
              <Input value={state.naming_convention || ''} onChange={e => setState({ ...state, naming_convention: e.target.value })} data-testid="drive-naming-input" />
            </label>
            <div className="mt-3 flex justify-between">
              <Button size="sm" variant="outline" onClick={() => setStep(1)}>Back</Button>
              <Button size="sm" onClick={() => saveStep(3, { naming_convention: state.naming_convention })} disabled={saving} data-testid="drive-step-2-next">Save &amp; continue</Button>
            </div>
          </section>
        )}

        {/* STEP 3 — access template */}
        {step === 3 && (
          <section className="card p-5" data-testid="drive-step-3">
            <div className="font-heading font-semibold flex items-center gap-2"><ShieldCheck size={16} />Access template (per subfolder × role)</div>
            <div className="text-[13px] text-[hsl(var(--foreground-muted))] mt-0.5">Applies at project creation. edit / view / none.</div>
            <div className="mt-3 overflow-x-auto card">
              <table className="tbl min-w-[900px]" data-testid="drive-access-grid">
                <thead><tr><th>Subfolder</th>{ROLES.map(r => <th key={r} className="text-center text-[11px] capitalize">{r.replace('_', ' ')}</th>)}</tr></thead>
                <tbody>
                  {(state.folders || []).map(f => (
                    <tr key={f}>
                      <td className="font-mono text-[12px]">{f}</td>
                      {ROLES.map(r => (
                        <td key={r} className="text-center">
                          <select className="h-7 text-[11.5px] border rounded px-1" style={{ borderColor: 'hsl(var(--border))' }}
                                  value={(state.access_template[f] || {})[r] || 'none'}
                                  onChange={e => patchGrid(f, r, e.target.value)}
                                  data-testid={`drive-grid-${f}-${r}`}>
                            {LEVELS.map(L => <option key={L} value={L}>{L}</option>)}
                          </select>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 flex justify-between">
              <Button size="sm" variant="outline" onClick={() => setStep(2)}>Back</Button>
              <Button size="sm" onClick={() => saveStep(4)} data-testid="drive-step-3-next">Continue</Button>
            </div>
          </section>
        )}

        {/* STEP 4 — team groups */}
        {step === 4 && (
          <section className="card p-5" data-testid="drive-step-4">
            <div className="font-heading font-semibold flex items-center gap-2"><UsersIcon size={16} />Team groups (per role)</div>
            <div className="text-[13px] text-[hsl(var(--foreground-muted))] mt-0.5">Prefer a Google Group per role for hygiene. Otherwise list member emails.</div>
            <div className="mt-3 space-y-2">
              {ROLES.map(r => (
                <div key={r} className="grid grid-cols-[130px_1fr_1fr_auto] gap-2 items-end" data-testid={`drive-group-${r}`}>
                  <div className="text-[13px] capitalize">{r.replace('_', ' ')}</div>
                  <Input placeholder="google-group@32december.design" value={(teamGroupEdits[r]?.google_group) || ''} onChange={e => setTeamGroupEdits(prev => ({ ...prev, [r]: { ...(prev[r] || {}), google_group: e.target.value } }))} data-testid={`drive-group-${r}-google`} />
                  <Input placeholder="member1@…, member2@… (fallback)" value={(teamGroupEdits[r]?.member_emails) || ''} onChange={e => setTeamGroupEdits(prev => ({ ...prev, [r]: { ...(prev[r] || {}), member_emails: e.target.value } }))} />
                  <Button size="sm" variant="outline" onClick={() => saveTeamGroup(r)}>Save</Button>
                </div>
              ))}
            </div>
            <div className="mt-3 flex justify-between">
              <Button size="sm" variant="outline" onClick={() => setStep(3)}>Back</Button>
              <Button size="sm" onClick={() => saveStep(5)} data-testid="drive-step-4-next">Continue</Button>
            </div>
          </section>
        )}

        {/* STEP 5 — dry-run */}
        {step === 5 && (
          <section className="card p-5" data-testid="drive-step-5">
            <div className="font-heading font-semibold flex items-center gap-2"><PlayCircle size={16} />Test run</div>
            <div className="text-[13px] text-[hsl(var(--foreground-muted))] mt-0.5">Preview exactly what would be created when OAuth is active.</div>
            <div className="grid grid-cols-3 gap-2 mt-3">
              <label><span className="text-[12px] text-[hsl(var(--foreground-muted))]">Project code</span><Input value={mockInput.mock_project_code} onChange={e => setMockInput({ ...mockInput, mock_project_code: e.target.value })} /></label>
              <label><span className="text-[12px] text-[hsl(var(--foreground-muted))]">Client name</span><Input value={mockInput.mock_client_name} onChange={e => setMockInput({ ...mockInput, mock_client_name: e.target.value })} /></label>
              <label><span className="text-[12px] text-[hsl(var(--foreground-muted))]">Project name</span><Input value={mockInput.mock_project_name} onChange={e => setMockInput({ ...mockInput, mock_project_name: e.target.value })} /></label>
            </div>
            <div className="mt-3"><Button size="sm" onClick={runDry} data-testid="drive-dry-run"><PlayCircle size={13} className="mr-1" />Run preview</Button></div>
            {dryRun && (
              <div className="mt-4 border rounded-lg p-3 bg-[hsl(var(--surface-2))]" data-testid="drive-dry-run-result">
                <div className="text-[12px] text-[hsl(var(--foreground-muted))]">Root folder to be created</div>
                <div className="font-mono text-[13px] mt-0.5">{dryRun.root_folder_name}</div>
                {dryRun.root_parent_url && <a href={dryRun.root_parent_url} target="_blank" rel="noreferrer" className="text-[11.5px] text-[hsl(var(--primary))]">↗ under {dryRun.root_parent_url}</a>}
                <div className="mt-3 space-y-2">
                  {dryRun.subfolders.map(sf => (
                    <div key={sf.subfolder} className="border-b pb-2 last:border-b-0" style={{ borderColor: 'hsl(var(--border))' }}>
                      <div className="font-mono text-[12.5px]">{sf.subfolder}</div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {sf.access.map((a, i) => (
                          <span key={i} className={`chip ${a.level === 'edit' ? 'chip-success' : 'chip-info'} capitalize`} title={a.target}>
                            {a.role.replace('_', ' ')}: {a.level}
                            {a.target?.startsWith('(') && <span className="ml-1 text-[10px] text-[hsl(var(--foreground-subtle))]">↳ fallback</span>}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 text-[12px] text-[hsl(var(--foreground-muted))] inline-flex items-start gap-1"><AlertTriangle size={12} className="mt-0.5" />{dryRun.notes}</div>
              </div>
            )}
            <div className="mt-4 flex justify-between">
              <Button size="sm" variant="outline" onClick={() => setStep(4)}>Back</Button>
              <Button size="sm" onClick={() => saveStep(5, { completed: true })} disabled={saving} data-testid="drive-mark-complete"><CheckCircle2 size={13} className="mr-1" />Mark setup complete</Button>
            </div>
          </section>
        )}
      </div>
    </AppShell>
  );
}
